// Procedural pixel-art core. Sprites are authored as small character grids with
// a shared palette and a consistent top-left light direction, then baked once to
// nearest-neighbor textures (crisp, chunky pixels) and cached. The renderer uses
// these textures via Sprites, so swapping in a real atlas later means changing
// only the bake calls, not the world layer.

import { Texture } from 'pixi.js';

/** Shared palette. One light direction (top-left) for cohesion: lighter shades
 *  are highlights, darker are shadowed sides. '.' and ' ' are transparent. */
const PALETTE: Record<string, number> = {
  x: 0x14131c, // near-black outline
  X: 0x2a2735, // soft outline / deep shadow

  // grass / foliage
  g: 0x7cb262,
  G: 0x5d9447,
  v: 0x406e30,
  V: 0x2c4d22,

  // wood / trunk / dirt
  w: 0x9c6b3f,
  W: 0x74492a,
  k: 0x4c2f1b,
  e: 0xb98a55, // earth / path light

  // stone
  s: 0xc2c9d2,
  S: 0x97a0ab,
  z: 0x69727e,
  Z: 0x474e58,

  // ore vein
  y: 0xffe39a,
  Y: 0xf0a93a,

  // building walls (warm plaster / timber)
  b: 0xd8c39a,
  B: 0xb89a6e,
  n: 0x8a6f4c,
  N: 0x5f4a31,

  // roofs (multiple hues, chosen per building)
  r: 0xc25453,
  R: 0x8f3535, // red roof + shade
  u: 0x5f7fc8,
  U: 0x3d568f, // blue roof
  c: 0x9aa1ab,
  C: 0x646b75, // slate roof
  t: 0x6cae57,
  T: 0x478a3c, // thatch/green roof
  p: 0x9a6cc0,
  P: 0x6e459a, // purple (arcane)

  // glass / lit windows / water
  l: 0xffe9a8, // warm lit window
  L: 0xfff6cf,
  a: 0x7fc4e0, // cool glass / water
  A: 0x4f97c4,

  // metal / iron / weapon
  i: 0xb9c0c9,
  I: 0x848d99,

  // accents
  f: 0xffd24a, // gold / flag
  q: 0xc79bff, // cosmic violet
  o: 0xff8c3a, // fire / forge glow
  O: 0xffd060,

  // enemy palette (crimson)
  d: 0xd6433f,
  D: 0x8f2723,
  // defender palette (steel blue)
  m: 0x4f86d6,
  M: 0x2f568f,
  // skin
  h: 0xe7b58a,
  H: 0xc28a5f,
  // white
  '#': 0xf2f2f2,
};

const texCache = new Map<string, Texture>();

function hex(color: number): string {
  return '#' + color.toString(16).padStart(6, '0');
}

/** Bakes a character-grid sprite to a cached, crisp texture. `scale` is the
 *  integer pixel size; art authored at N px wide renders at N*scale on screen. */
export function bakeGrid(key: string, rows: string[], scale = 3): Texture {
  const cached = texCache.get(key);
  if (cached) return cached;

  // Trim trailing fully-transparent rows: with fractional anchors (units use
  // 0.85) a blank bottom row lifts the visible feet off the ground line.
  let art = rows;
  while (art.length && [...art[art.length - 1]].every((c) => PALETTE[c] === undefined)) {
    art = art.slice(0, -1);
  }

  const h = art.length;
  const w = art.reduce((m, r) => Math.max(m, r.length), 0);
  const canvas = document.createElement('canvas');
  canvas.width = w * scale;
  canvas.height = h * scale;
  const ctx = canvas.getContext('2d')!;
  ctx.imageSmoothingEnabled = false;

  for (let y = 0; y < h; y++) {
    const row = art[y];
    for (let x = 0; x < row.length; x++) {
      const color = PALETTE[row[x]];
      if (color === undefined) continue; // '.', ' ', unknown = transparent
      ctx.fillStyle = hex(color);
      ctx.fillRect(x * scale, y * scale, scale, scale);
    }
  }

  const tex = Texture.from(canvas);
  tex.source.scaleMode = 'nearest';
  texCache.set(key, tex);
  return tex;
}

/** Bakes an isometric ground diamond as a pixel-art tile: a base grass fill with
 *  a top highlight band, a shadowed lower half, a deterministic speckle for
 *  texture, and a darker rim. `seed` varies the speckle so tiles do not tile
 *  obviously. */
export function bakeGround(key: string, w: number, h: number, base: number, seed: number): Texture {
  const cached = texCache.get(key);
  if (cached) return cached;

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;
  ctx.imageSmoothingEnabled = false;

  const top = shade(base, 1.02);
  const bottom = shade(base, 0.98);

  const cx = w / 2;
  const cy = h / 2;
  const px = 2; // chunky 2px blocks for a pixel feel
  let n = (seed + 1) * 2654435761;
  const rnd = (): number => {
    n = (n ^ (n << 13)) >>> 0;
    n = (n ^ (n >> 17)) >>> 0;
    n = (n ^ (n << 5)) >>> 0;
    return (n >>> 0) / 0xffffffff;
  };

  for (let y = 0; y < h; y += px) {
    for (let x = 0; x < w; x += px) {
      // inside the iso diamond?  |dx|/(w/2) + |dy|/(h/2) <= 1
      const dx = Math.abs(x + px / 2 - cx) / (w / 2);
      const dy = Math.abs(y + px / 2 - cy) / (h / 2);
      const edge = dx + dy;
      if (edge > 1) continue;

      let col = y < cy ? top : bottom;
      if (rnd() < 0.07) col = shade(base, 1.08); // light speckle
      else if (rnd() < 0.07) col = shade(base, 0.9); // dark speckle

      ctx.fillStyle = hex(col);
      ctx.fillRect(x, y, px, px);
    }
  }

  const tex = Texture.from(canvas);
  tex.source.scaleMode = 'nearest';
  texCache.set(key, tex);
  return tex;
}

/** Per-era sky gradients, horizon placed where the board sits on screen. The
 *  "ebedi mavi gök" band glows behind the steppe; the space age goes near-black
 *  with a violet band and a baked pixel starfield (no per-frame cost). Each
 *  entry: [top, above-horizon (offset from horizon), horizon, bottom]. */
const SKY_STOPS: [number, string][][] = [
  [
    [0, '#526f79'],
    [-0.15, '#a4b6ad'],
    [0, '#e8c995'],
    [1, '#384f51'],
  ],
  [
    [0, '#355c79'],
    [-0.15, '#82adbb'],
    [0, '#c9d8ca'],
    [1, '#344e53'],
  ],
  [
    [0, '#46345e'],
    [-0.15, '#ae7b85'],
    [0, '#f2a65e'],
    [1, '#403b4e'],
  ],
  [
    [0, '#090d14'],
    [-0.15, '#15202e'],
    [0, '#2a3a4e'],
    [1, '#0c1018'],
  ],
  [
    [0, '#06070d'],
    [-0.18, '#0b0a18'],
    [0, '#2a2344'],
    [1, '#08070f'],
  ],
];

export function bakeSky(era: number, horizonFrac: number, aspect = 1, age = 5): Texture {
  const e = Math.max(0, Math.min(era, SKY_STOPS.length - 1));
  const h = Math.max(0.2, Math.min(0.8, horizonFrac));
  const key = `sky:${e}:${Math.round(h * 20)}:${Math.round(aspect * 100)}:${Math.min(age,11)}`;
  const cached = texCache.get(key);
  if (cached) return cached;

  const W = 160;
  const H = 256;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d')!;
  const grad = ctx.createLinearGradient(0, 0, 0, H);
  const futureSkies = [
    ['#100f16','#32293b','#64523a','#161820'],
    ['#080f24','#142b48','#2c6472','#0c1529'],
    ['#130b20','#332047','#693a69','#100c20'],
    ['#274664','#517d99','#b1d5d7','#315675'],
    ['#353045','#797083','#e4d6a4','#514967'],
  ];
  const late = age>=7 ? futureSkies[Math.min(age,11)-7] : null;
  const stops: [number,string][] = late ? [[0,late[0]],[-.15,late[1]],[0,late[2]],[1,late[3]]] : SKY_STOPS[e];
  grad.addColorStop(0, stops[0][1]);
  grad.addColorStop(Math.max(0.02, h + stops[1][0]), stops[1][1]);
  grad.addColorStop(h, stops[2][1]);
  grad.addColorStop(1, stops[3][1]);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  if (e < 4) {
    // Quiet stepped ridgelines and long cloud banks frame the settlement.
    ctx.fillStyle = e === 2 ? '#ffdca0' : '#fff0c8';
    for (let dy=-7;dy<=7;dy++) {
      const half=Math.floor(Math.sqrt(49-dy*dy) * W / (H * Math.max(.2, aspect)));
      ctx.fillRect(119-half,Math.round(h*H*.5)+dy,half*2+1,1);
    }
    for(let layer=0;layer<3;layer++) {
      ctx.fillStyle = (e===2 ? ['#a8797e','#796d77','#535d64'] : ['#91a39a','#768f85','#526e69'])[layer];
      for(let x=0;x<W;x++) {
        const y=Math.round(h*H-18+layer*9+Math.sin(x*.054+layer*2)*7+Math.sin(x*.13+layer)*3);
        ctx.fillRect(x,y,1,Math.max(0,Math.round(h*H+15)-y));
      }
    }
    ctx.fillStyle=e===2?'#d6afa0':'#d5d6bc';
    for(let i=0;i<5;i++) {
      const x=12+i*33,y=17+(i*13)%31;
      ctx.fillRect(x,y,17,1);ctx.fillRect(x+4,y-1,9,1);
    }
  }

  if (e >= 4) {
    // Baked starfield: chunky nearest-neighbor pixels that match the art.
    let n = 77777;
    const rnd = (): number => {
      n = (n ^ (n << 13)) >>> 0;
      n = (n ^ (n >> 17)) >>> 0;
      n = (n ^ (n << 5)) >>> 0;
      return (n >>> 0) / 0xffffffff;
    };
    const colors = ['#f2f2f2', '#9fd0ff', '#c79bff'];
    for (let i = 0; i < 90; i++) {
      const x = Math.floor(rnd() * W);
      const y = Math.floor(rnd() * H * 0.55);
      ctx.globalAlpha = 0.4 + rnd() * 0.5;
      ctx.fillStyle = colors[Math.floor(rnd() * colors.length)];
      const s = rnd() < 0.2 ? 2 : 1;
      ctx.fillRect(x, y, s, s);
    }
    ctx.globalAlpha = 1;
  }

  const tex = Texture.from(canvas);
  tex.source.scaleMode = 'nearest';
  texCache.set(key, tex);
  return tex;
}

/** Multiplies an 0xRRGGBB colour by a factor (clamped). */
export function shade(color: number, factor: number): number {
  const r = Math.min(255, Math.round(((color >> 16) & 0xff) * factor));
  const g = Math.min(255, Math.round(((color >> 8) & 0xff) * factor));
  const b = Math.min(255, Math.round((color & 0xff) * factor));
  return (r << 16) | (g << 8) | b;
}
